<script setup>
import { ref, watch, computed } from 'vue';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import AutoComplete from 'primevue/autocomplete';
import Select from 'primevue/select';
import Button from 'primevue/button';
import { useToast } from 'primevue/usetoast';

import { useContractsStore } from '@/stores/contract.js';
import { useFacilityStore } from '@/stores/facility.js';

const props = defineProps({
  visible: { type: Boolean, default: false },
  // если задан — поле Объект зафиксировано, инициализация из id
  presetFacilityId: { type: [Number, String], required: false, default: null },
  // если задан — режим редактирования
  contractId: { type: [Number, String], required: false, default: null }
});
const emit = defineEmits(['update:visible', 'close', 'created', 'updated']);

const toast = useToast();
const contractsStore = useContractsStore();
const facilityStore = useFacilityStore();

// Поля формы
const numberInfo = ref({ sequence: null, date: '', number: '' });
const selectedFacility = ref(null); // { id, label, short_name, address }
const facilityQueryResults = ref([]);
const selectedCustomerId = ref(null);
const customerOptions = ref([]); // [{ label, value }]

const isEditMode = computed(() => !!props.contractId);
const facilityLocked = computed(() => !!props.presetFacilityId || isEditMode.value);

// Предзагрузка номера и даты + инициализация facility по preset
watch(
    () => props.visible,
    async (vis) => {
      if (!vis) return;
      try {
        if (isEditMode.value) {
          // Режим редактирования: загружаем данные договора
          await loadContractData(Number(props.contractId));
        } else {
          // Режим создания: получить номер/дату
          const info = await contractsStore.fetchNextNumber();
          numberInfo.value = {
            sequence: info?.sequence ?? null,
            date: info?.date ?? '',
            number: info?.number ?? ''
          };

          // если preset facility — подтянем объект и связанные заказчики
          if (props.presetFacilityId) {
            await initFacilityById(Number(props.presetFacilityId));
          } else {
            // очистить форму при обычном открытии
            selectedFacility.value = null;
            selectedCustomerId.value = null;
            customerOptions.value = [];
          }
        }
      } catch (e) {
        console.error('Ошибка инициализации формы договора', e);
        toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Не удалось подготовить форму', life: 4000 });
      }
    },
    { immediate: false }
);

async function loadContractData(id) {
  try {
    const { default: contractApi } = await import('@/api/contractApi.js');
    const contract = await contractApi.getContractDetail(id);

    // Заполняем номер и дату
    numberInfo.value = {
      sequence: contract?.sequence ?? null,
      date: contract?.date ?? '',
      number: contract?.number ?? ''
    };

    // Заполняем объект (блокируем его для редактирования)
    const facilityId = contract?.facility?.id ?? contract?.facility_id;
    if (facilityId) {
      await initFacilityById(Number(facilityId));
    }

    // Заполняем заказчика (должен быть выбран, но можно изменить)
    selectedCustomerId.value = contract?.customer_id ?? null;
  } catch (e) {
    console.error('Ошибка загрузки данных договора', e);
    toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Не удалось загрузить договор', life: 4000 });
  }
}

async function initFacilityById(id) {
  try {
    const { default: facilityApi } = await import('@/api/facilityApi.js');
    const f = await facilityApi.getFacility(id);
    selectedFacility.value = f ? {
      id: f.id,
      label: f.short_name || f.name || f.item_short || `Объект #${f.id}`,
      short_name: f.short_name || f.item_short || '',
      address: f.address || f.item_address || ''
    } : null;
    await loadCustomersForFacility(id);
  } catch (e) {
    console.error('Не удалось загрузить объект по ID', e);
    selectedFacility.value = null;
    customerOptions.value = [];
  }
}

// Автокомплит объектов
let searchTimer = null;
async function completeFacility(e) {
  const q = String(e?.query || '').trim();
  if (searchTimer) clearTimeout(searchTimer);
  if (!q) {
    facilityQueryResults.value = [];
    return;
  }
  searchTimer = setTimeout(async () => {
    try {
      const resp = await facilityStore.loadFacilities({ page: 1, search: q });
      const list = Array.isArray(resp?.results) ? resp.results : [];
      facilityQueryResults.value = list.map(x => ({
        id: x.id,
        label: x.short_name || x.item_short || x.name || x.item_name || `Объект #${x.id}`,
        short_name: x.short_name || x.item_short || '',
        address: x.address || x.item_address || ''
      }));
    } catch (err) {
      console.error('Ошибка поиска объектов', err);
      facilityQueryResults.value = [];
    }
  }, 300);
}

async function onFacilitySelect(opt) {
  selectedFacility.value = opt;
  selectedCustomerId.value = null;
  await loadCustomersForFacility(opt?.id);
}

async function loadCustomersForFacility(facilityId) {
  customerOptions.value = [];
  const currentCustomerId = selectedCustomerId.value; // сохраняем текущий выбор
  selectedCustomerId.value = null;

  if (!facilityId) return;

  try {
    const list = await facilityStore.loadFacilityParticipants(facilityId, { force: true });
    // Берём все релевантные роли: Заказчик, Генподрядчик, Подрядчик
    const allowed = new Set(['CUSTOMER', 'GENERAL_CONTRACTOR', 'CONTRACTOR']);
    const byId = new Map();

    (Array.isArray(list) ? list : []).forEach(p => {
      if (!allowed.has(p?.role)) return;
      const id = p?.customer_id;
      if (!id) return;

      let roleLabel = '';
      if (p.role === 'CUSTOMER') roleLabel = 'Заказчик';
      else if (p.role === 'GENERAL_CONTRACTOR') roleLabel = 'Генподрядчик';
      else if (p.role === 'CONTRACTOR') roleLabel = 'Подрядчик';

      const baseName = p.short_name || p?.customer_short_name || `Клиент #${id}`;
      const label = `${baseName} (${roleLabel})`;

      // Исключаем дубли по одному и тому же customer_id
      if (!byId.has(id)) {
        byId.set(id, { label, value: id });
      }
    });

    customerOptions.value = Array.from(byId.values());

    // Восстанавливаем выбор, если он есть в списке
    if (currentCustomerId && customerOptions.value.some(opt => opt.value === currentCustomerId)) {
      selectedCustomerId.value = currentCustomerId;
    }
    // если один вариант — автоподставим
    else if (customerOptions.value.length === 1) {
      selectedCustomerId.value = customerOptions.value[0].value;
    }
  } catch (e) {
    console.error('Ошибка загрузки участников объекта', e);
    customerOptions.value = [];
  }
}


function close() {
  emit('update:visible', false);
  emit('close');
}

async function submit() {
  if (!selectedFacility.value?.id) {
    toast.add({ severity: 'warn', summary: 'Заполните форму', detail: 'Выберите объект', life: 2500 });
    return;
  }
  if (!selectedCustomerId.value) {
    toast.add({ severity: 'warn', summary: 'Заполните форму', detail: 'Выберите заказчика', life: 2500 });
    return;
  }

  try {
    if (isEditMode.value) {
      // Режим редактирования: обновляем только заказчика
      const updated = await contractsStore.updateContract(Number(props.contractId), {
        customer: selectedCustomerId.value
      });
      toast.add({ severity: 'success', summary: 'Договор обновлен', detail: numberInfo.value.number || 'Успешно', life: 2500 });
      emit('updated', updated);
    } else {
      // Режим создания
      const created = await contractsStore.createContract({
        facility: selectedFacility.value.id,
        customer: selectedCustomerId.value
      });
      toast.add({ severity: 'success', summary: 'Договор создан', detail: numberInfo.value.number || 'Успешно', life: 2500 });
      emit('created', created);
    }
    close();
  } catch (e) {
    console.error('Ошибка сохранения договора', e);
    const detail = e?.response?.data?.detail || 'Не удалось сохранить договор';
    toast.add({ severity: 'error', summary: 'Ошибка', detail, life: 4000 });
  }
}

const dialogHeader = computed(() => isEditMode.value ? 'Редактировать договор' : 'Добавить договор');
const submitButtonLabel = computed(() => isEditMode.value ? 'Сохранить' : 'Добавить');
</script>

<template>
  <Dialog
      :visible="visible"
      @update:visible="(v)=> v ? null : close()"
      modal
      :header="dialogHeader"
      :style="{ width: '680px', maxWidth: '95vw' }"
      :closable="true"
      :position="'top'"
  >
    <form class="flex flex-col gap-5" @submit.prevent="submit">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <InputText id="number" :value="numberInfo.number" disabled class="w-full" />
        <InputText id="date" :value="numberInfo.date" disabled class="w-full" />
      </div>

      <div class="flex flex-col gap-4">
        <AutoComplete
            inputId="facility"
            v-model="selectedFacility"
            optionLabel="label"
            :suggestions="facilityQueryResults"
            @complete="completeFacility"
            @item-select="(e)=>onFacilitySelect(e.value)"
            :pt="{ input: { class: 'w-full' } }"
            :disabled="facilityLocked"
            placeholder="Выберите объект"
        />

        <div v-if="selectedFacility?.address" class="text-xs text-color-secondary">
          {{ selectedFacility.address }}
        </div>
        <Select
            inputId="customer"
            v-model="selectedCustomerId"
            :options="customerOptions"
            optionLabel="label"
            optionValue="value"
            class="w-full"
            :disabled="!selectedFacility"
            placeholder="Выберите контрагента"
        />
      </div>

      <div class="flex justify-center gap-3 pt-2">
        <Button :label="submitButtonLabel" type="submit" severity="info" rounded />
        <Button label="Отмена" type="button" severity="secondary" rounded @click="close" />
      </div>
    </form>
  </Dialog>
</template>

<style scoped>
:deep(.p-autocomplete) { display: flex; width: 100%; }
:deep(.p-autocomplete .p-autocomplete-input) { width: 100%; }
</style>